import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

function fmtDate(dt) {
  return new Date(dt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function isVideoUrl(url) {
  return /\.(mp4|webm|mov|avi|mkv)$/i.test(url || '');
}

export default function AdminNews() {
  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ title: '', body: '', media_url: '' });
  const [posting, setPosting] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI.newsList()
      .then(r => setNews(r.data.news || []))
      .catch(() => toast.error('Failed to load news'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePost = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setPosting(true);
    try {
      await adminAPI.createNews(form);
      toast.success('News posted successfully!');
      setForm({ title: '', body: '', media_url: '' });
      setPreview(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post news');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news post? This will also remove all likes and comments on it.')) return;
    try {
      await adminAPI.deleteNews(id);
      toast.success('News deleted');
      setNews(n => n.filter(x => x.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const mediaUrl = form.media_url.trim();

  return (
    <div>
      {/* ── Post Form ── */}
      <div className="table-card" style={{ marginBottom: 24 }}>
        <div className="table-header">
          <h3>Post a News Update</h3>
        </div>
        <div style={{ padding: 20 }}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              className="form-control"
              value={form.title}
              onChange={e => setF('title', e.target.value)}
              placeholder="Enter a headline or title..."
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Body Text</label>
            <textarea
              className="form-control"
              value={form.body}
              onChange={e => setF('body', e.target.value)}
              placeholder="Write the news content here. Leave blank if you're posting media only."
              rows={5}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Catbox Media Link (Image or Video URL)</label>
            <input
              className="form-control"
              value={form.media_url}
              onChange={e => { setF('media_url', e.target.value); setPreview(false); }}
              placeholder="https://files.catbox.moe/abc123.jpg  or  .mp4"
            />
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
              Upload your image or video to catbox.moe and paste the link here. Video files (mp4, webm, mov) are auto-detected.
            </div>

            {mediaUrl && (
              <div style={{ marginTop: 12 }}>
                {!preview ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPreview(true)}
                  >
                    Preview media
                  </button>
                ) : (
                  <div style={{ borderRadius: 8, overflow: 'hidden', maxHeight: 280, background: 'var(--gray-100)' }}>
                    {isVideoUrl(mediaUrl) ? (
                      <video
                        src={mediaUrl}
                        controls
                        style={{ width: '100%', maxHeight: 280, display: 'block' }}
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={mediaUrl}
                        alt="media preview"
                        style={{ width: '100%', maxHeight: 280, objectFit: 'cover', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; toast.error('Could not load image — check the URL'); }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              className="btn btn-ghost"
              onClick={() => { setForm({ title: '', body: '', media_url: '' }); setPreview(false); }}
              disabled={posting}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePost}
              disabled={posting || !form.title.trim()}
            >
              {posting ? 'Posting…' : 'Post News'}
            </button>
          </div>
        </div>
      </div>

      {/* ── News List ── */}
      <div className="table-card">
        <div className="table-header">
          <h3>All News Posts</h3>
          <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>{news.length} post{news.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Loading…</div>
        ) : news.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)', fontSize: 14 }}>
            No news posted yet. Use the form above to post the first update.
          </div>
        ) : (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {news.map(item => (
              <div
                key={item.id}
                style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: 'white',
                }}
              >
                {/* Media thumbnail */}
                {item.media_url && (
                  <div style={{ background: 'var(--gray-100)', lineHeight: 0, maxHeight: 220, overflow: 'hidden' }}>
                    {isVideoUrl(item.media_url) || item.media_type === 'video' ? (
                      <video
                        src={item.media_url}
                        controls
                        preload="metadata"
                        style={{ width: '100%', maxHeight: 220, display: 'block' }}
                      />
                    ) : (
                      <img
                        src={item.media_url}
                        alt={item.title}
                        style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }}
                        onError={e => { e.target.parentElement.style.display = 'none'; }}
                      />
                    )}
                  </div>
                )}

                <div style={{ padding: 16 }}>
                  {/* Title + delete */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, lineHeight: 1.3 }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                        <span>{fmtDate(item.created_at)}</span>
                        <span>By {item.created_by_name || 'Admin'}</span>
                        <span style={{ color: 'var(--blue-mid)', fontWeight: 600 }}>
                          {item.like_count} like{item.like_count !== 1 ? 's' : ''}
                        </span>
                        <span style={{ color: 'var(--gray-600)', fontWeight: 600 }}>
                          {item.comment_count} comment{item.comment_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item.id)}
                      style={{ flexShrink: 0 }}
                    >
                      Delete
                    </button>
                  </div>

                  {/* Body */}
                  {item.body && (
                    <p style={{
                      fontSize: 14, color: 'var(--gray-600)',
                      lineHeight: 1.65, whiteSpace: 'pre-wrap',
                      margin: 0, paddingTop: 4,
                    }}>
                      {item.body}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
